// Utility helpers for generating PDF documents with jsPDF.
// Currently only used for rendering facility / clinic information in invoices,
// but can be extended with other shared PDF logic.

/**
 * Checks whether a field coming from office setting should be displayed.
 * The office-setting schema stores every field as an array of shape
 *     [value, isVisible]
 * Matching the logic from ClinicInformation.js.
 */
export const shouldDisplay = (data) => Array.isArray(data) && data[1] === true;

/**
 * Renders facility / clinic information in the **left column** of a two-column
 * invoice layout.
 *
 * Parameters
 *   pdf          – jsPDF instance
 *   officeData   – object containing facility details (matches backend schema)
 *   options      – {
 *                    startX: number  (x-position where text starts)
 *                    startY: number  (y-position where section header is drawn)
 *                    lineHeight: number (baseline-to-baseline distance)
 *                    columnWidth: number (max text width before wrapping)
 *                 }
 *
 * Returns         – newY (number) – the Y-coordinate **after** the facility
 *                   section so callers can update their cursor.
 */
export const renderFacilityInformation = (
  pdf,
  officeData,
  { startX, startY, lineHeight = 7, columnWidth = 80 }
) => {
  if (!officeData) return startY; // nothing to render

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('Facility Information', startX, startY);

  let y = startY + lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  // Helper function to write a single line and advance y
  const writeLine = (text) => {
    pdf.text(text, startX, y);
    y += lineHeight * 0.8;
  };

  // Facility Name (always show if available)
  if (officeData.facility_name && officeData.facility_name[0]) {
    writeLine(`Facility Name: ${officeData.facility_name[0]}`);
  }

  if (shouldDisplay(officeData.speciality_type)) {
    writeLine(`Practice Speciality: ${officeData.speciality_type[0]}`);
  }

  if (shouldDisplay(officeData.facility_address)) {
    const addrLines = pdf.splitTextToSize(
      `Facility Address: ${officeData.facility_address[0]}`,
      columnWidth
    );
    addrLines.forEach((l) => writeLine(l));
  }

  if (shouldDisplay(officeData.city)) {
    writeLine(`City: ${officeData.city[0]}`);
  }
  if (shouldDisplay(officeData.state)) {
    writeLine(`State: ${officeData.state[0]}`);
  }
  if (shouldDisplay(officeData.country)) {
    writeLine(`Country: ${officeData.country[0]}`);
  }
  if (shouldDisplay(officeData.zipcode)) {
    writeLine(`Zip Code: ${officeData.zipcode[0]}`);
  }
  if (shouldDisplay(officeData.office_phone)) {
    writeLine(`Office Phone: ${officeData.office_phone[0]}`);
  }
  if (shouldDisplay(officeData.office_fax)) {
    writeLine(`Office Fax: ${officeData.office_fax[0]}`);
  }
  if (shouldDisplay(officeData.buisness_email)) {
    writeLine(`Business Email: ${officeData.buisness_email[0]}`);
  }

  if (shouldDisplay(officeData.additional_basic_data_1)) {
    writeLine(`Data I: ${officeData.additional_basic_data_1[0]}`);
  }
  if (shouldDisplay(officeData.additional_basic_data_2)) {
    writeLine(`Data II: ${officeData.additional_basic_data_2[0]}`);
  }
  if (shouldDisplay(officeData.additional_basic_data_3)) {
    writeLine(`Data III: ${officeData.additional_basic_data_3[0]}`);
  }

  if (shouldDisplay(officeData.exam_rooms)) {
    writeLine('Exam Rooms:');
    Object.entries(officeData.exam_rooms).forEach(([room, value]) => {
      const roomText = `  ${room}: ${value}`;
      const rLines = pdf.splitTextToSize(roomText, columnWidth - 10);
      rLines.forEach((l) => {
        pdf.text(l, startX + 5, y);
        y += lineHeight * 0.7;
      });
    });
  }

  if (shouldDisplay(officeData.operating_hrs)) {
    const hrs = officeData.operating_hrs[0];
    const hrsLines = pdf.splitTextToSize(
      `Operating Hours: ${hrs.start_time} to ${hrs.end_time}`,
      columnWidth
    );
    hrsLines.forEach((l) => writeLine(l));
  }

  return y; // return y position after writing facility info
}; 